/**
 * Brand alignment breakdown modal.
 *
 * Shown when the user clicks the "Brand alignment XX%" badge in the blog
 * editor sidebar. Explains exactly why the score isn't 100% (missing
 * keywords, banned words, missing CTA, length issues) and offers an
 * "Apply with AI" button that calls /api/blog/align, replaces the post's
 * content_html in memory, and surfaces an undo toast so the user can
 * revert if they don't like the rewrite.
 */
import { client } from "../api/client.js";
import { escapeHtml } from "./escapeHtml.js";
import { toast } from "./toast.js";

const CHECK_ICONS = {
    keywords: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 12 2 2 4-4"/><circle cx="12" cy="12" r="10"/></svg>',
    avoid_words: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M4.9 4.9l14.2 14.2"/></svg>',
    cta: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/></svg>',
    length: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="15" y2="18"/></svg>',
};

function renderCheck(check) {
    const passClass = check.pass ? 'bsm-check--pass' : (check.points > 0 ? 'bsm-check--partial' : 'bsm-check--fail');
    const icon = CHECK_ICONS[check.check] || '';
    const label = escapeHtml(check.label || check.check);
    const max = check.max != null ? ` / ${check.max >= 0 ? '+' : ''}${check.max}` : '';
    const pointsText = check.points >= 0 ? `+${check.points}` : `${check.points}`;

    // Detail body — varies per check type
    let detailHtml = '';
    const d = check.detail || {};
    if (check.check === 'keywords') {
        if (d.missing && d.missing.length > 0) {
            detailHtml += `<div class="bsm-chip-row"><span class="bsm-chip-label">Missing:</span>${d.missing.map((k) => `<span class="bsm-chip bsm-chip--missing">${escapeHtml(k)}</span>`).join('')}</div>`;
        }
        if (d.present && d.present.length > 0) {
            detailHtml += `<div class="bsm-chip-row"><span class="bsm-chip-label">Present:</span>${d.present.map((k) => `<span class="bsm-chip bsm-chip--present">${escapeHtml(k)}</span>`).join('')}</div>`;
        }
    } else if (check.check === 'avoid_words') {
        if (d.words && d.words.length > 0) {
            detailHtml += `<div class="bsm-chip-row"><span class="bsm-chip-label">Found:</span>${d.words.map((w) => `<span class="bsm-chip bsm-chip--banned">${escapeHtml(w)}</span>`).join('')}</div>`;
        }
    } else if (check.check === 'cta') {
        if (d.signals && d.signals.length > 0) {
            detailHtml += `<div class="bsm-chip-row"><span class="bsm-chip-label">Signals:</span>${d.signals.map((s) => `<span class="bsm-chip bsm-chip--signal">${escapeHtml(s)}</span>`).join('')}</div>`;
        }
        if (d.brandCta) {
            detailHtml += `<div class="bsm-hint-line">Brand CTA: <code>${escapeHtml(d.brandCta)}</code> ${d.brandCtaInContent ? '<span class="bsm-chip bsm-chip--present">used</span>' : '<span class="bsm-chip bsm-chip--missing">not used</span>'}</div>`;
        }
    } else if (check.check === 'length') {
        detailHtml += `<div class="bsm-hint-line">Current: <strong>${d.words || 0}</strong> words. Ideal: 500–1500.</div>`;
    }

    return `
        <div class="bsm-check ${passClass}">
            <div class="bsm-check-head">
                <span class="bsm-check-icon">${icon}</span>
                <span class="bsm-check-label">${label}</span>
                <span class="bsm-check-points">${pointsText}${max}</span>
            </div>
            <div class="bsm-check-advice">${escapeHtml(check.advice || '')}</div>
            ${detailHtml}
        </div>
    `;
}

export function openBrandScoreModal({ score, checks, post, onApplied, onClose } = {}) {
    closeBrandScoreModal();

    const overlay = document.createElement('div');
    overlay.id = 'brand-score-modal-overlay';
    overlay.className = 'bsm-overlay';
    const tier = score >= 80 ? 'high' : score >= 50 ? 'mid' : 'low';
    const isFull = score >= 100;

    overlay.innerHTML = `
        <div class="bsm-shell" role="dialog" aria-label="Brand alignment breakdown">
            <div class="bsm-topbar">
                <div class="bsm-topbar-left">
                    <div class="bsm-topbar-title">Brand alignment</div>
                    <div class="bsm-topbar-sub">Why your post scored ${score}% — and how to fix it</div>
                </div>
                <div class="bsm-score-ring bsm-score-ring--${tier}">
                    <span class="bsm-score-value">${score}%</span>
                </div>
                <button class="bsm-close" id="bsm-close" aria-label="Close">&times;</button>
            </div>
            <div class="bsm-body">
                ${isFull ? '<div class="bsm-fully-aligned">✓ This post is fully aligned with your brand. No changes recommended.</div>' : ''}
                <div class="bsm-checks">${(checks || []).map(renderCheck).join('')}</div>
            </div>
            <div class="bsm-footer">
                <button class="btn btn-ghost btn-sm" id="bsm-close-btn">Close</button>
                ${isFull ? '' : `
                    <button class="btn btn-primary btn-sm" id="bsm-apply-btn" data-testid="brand-score-apply">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
                        Apply with AI
                    </button>
                `}
            </div>
        </div>
    `;
    document.body.appendChild(overlay);

    const close = () => {
        closeBrandScoreModal();
        onClose && onClose();
    };
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
    overlay.querySelector('#bsm-close').addEventListener('click', close);
    overlay.querySelector('#bsm-close-btn').addEventListener('click', close);
    const esc = (e) => { if (e.key === 'Escape') { close(); document.removeEventListener('keydown', esc); } };
    document.addEventListener('keydown', esc);

    const applyBtn = overlay.querySelector('#bsm-apply-btn');
    if (applyBtn) {
        applyBtn.addEventListener('click', async () => {
            applyBtn.disabled = true;
            applyBtn.innerHTML = '<span class="bsm-spinner"></span> Rewriting with AI…';
            try {
                const res = await client.alignBlogPost({
                    title: post?.title || '',
                    content_html: post?.content_html || '',
                    brandChecks: checks,
                    brandScore: score,
                });
                const draft = res?.draft;
                if (!res?.ok || !draft) {
                    toast('error', res?.error || 'AI rewrite failed');
                    applyBtn.disabled = false;
                    applyBtn.innerHTML = 'Apply with AI';
                    return;
                }
                if (draft.warning) {
                    toast('warn', draft.warning);
                }
                // Hand the rewritten draft back to the caller. The caller
                // handles undo state + DOM update + dirty flag + toast.
                onApplied && onApplied(draft);
                closeBrandScoreModal();
            } catch (err) {
                console.error('[bsm apply]', err);
                toast('error', err.message || 'AI rewrite failed');
                applyBtn.disabled = false;
                applyBtn.innerHTML = 'Apply with AI';
            }
        });
    }
}

export function closeBrandScoreModal() {
    const existing = document.getElementById('brand-score-modal-overlay');
    if (existing) existing.remove();
}
